import { Button } from "@d12labs/ui/button";
import { Input } from "@d12labs/ui/input";
import { Label } from "@d12labs/ui/label";
import { Textarea } from "@d12labs/ui/textarea";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { StretchHorizontal } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { fitstreakApi } from "../api";
import { announceBadges } from "../components/announce-badges";
import { FormShell, WhenField } from "../components/form-shell";

const PRESET_AREAS = [
	"hips",
	"hamstrings",
	"t-spine",
	"shoulders",
	"ankles",
	"hip flexors",
	"calves",
	"glutes",
	"lower back",
	"neck",
];

export function LogMobilityPage() {
	const navigate = useNavigate();
	const qc = useQueryClient();
	const [areas, setAreas] = useState<string[]>([]);
	const [score, setScore] = useState(6);
	const [durationMin, setDurationMin] = useState("");
	const [when, setWhen] = useState("");
	const [notes, setNotes] = useState("");

	const toggle = (area: string) =>
		setAreas((cur) => (cur.includes(area) ? cur.filter((a) => a !== area) : [...cur, area]));

	const save = useMutation({
		mutationFn: () =>
			fitstreakApi.logMobility({
				input: {
					flexibilityScore: score,
					focusAreas: areas,
					startTime: when || undefined,
					notes: notes || undefined,
					durationSec: durationMin ? Math.round(Number(durationMin) * 60) : undefined,
				},
			}),
		onSuccess: (res) => {
			toast.success("Mobility session logged");
			announceBadges(res.newBadges);
			qc.invalidateQueries({ queryKey: ["fitstreak"] });
			navigate({ to: "/fitstreak" });
		},
		meta: { errorMessage: "Failed to log session" },
	});

	return (
		<FormShell
			icon={StretchHorizontal}
			title="Log mobility"
			subtitle="Track focus areas and rate how your flexibility felt today."
			actions={
				<>
					<Button variant="ghost" size="sm" onClick={() => navigate({ to: "/fitstreak" })}>
						Cancel
					</Button>
					<Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
						Log session
					</Button>
				</>
			}
		>
			<div className="flex flex-col gap-2">
				<Label>Focus areas</Label>
				<div className="flex flex-wrap gap-2">
					{PRESET_AREAS.map((area) => {
						const on = areas.includes(area);
						return (
							<button
								key={area}
								type="button"
								onClick={() => toggle(area)}
								className={`rounded-full border px-3 py-1 text-body-sm transition-colors ${
									on
										? "border-primary bg-primary/10 text-primary"
										: "border-hairline bg-surface-1 text-ink-muted hover:border-hairline-strong"
								}`}
							>
								{area}
							</button>
						);
					})}
				</div>
			</div>

			<div className="flex flex-col gap-2">
				<div className="flex items-center justify-between">
					<Label>Flexibility today</Label>
					<span className="text-sm font-medium tabular-nums text-primary">{score}/10</span>
				</div>
				<div className="flex gap-1">
					{Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
						<button
							key={n}
							type="button"
							onClick={() => setScore(n)}
							aria-label={`Rate flexibility ${n}`}
							className={`h-9 flex-1 rounded-md border text-sm font-medium tabular-nums transition-colors ${
								n <= score
									? "border-primary bg-primary/10 text-primary"
									: "border-hairline bg-surface-1 text-ink-subtle hover:border-hairline-strong"
							}`}
						>
							{n}
						</button>
					))}
				</div>
			</div>

			<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
				<WhenField value={when} onChange={setWhen} />
				<div className="flex flex-col gap-1.5">
					<Label htmlFor="duration">Duration (min, optional)</Label>
					<Input
						id="duration"
						type="number"
						min={1}
						value={durationMin}
						onChange={(e) => setDurationMin(e.target.value)}
					/>
				</div>
			</div>

			<div className="flex flex-col gap-1.5">
				<Label htmlFor="notes">Notes (optional)</Label>
				<Textarea id="notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
			</div>
		</FormShell>
	);
}
